import Vue from 'vue'
import Vuex from 'vuex'
import { authAPI, productsAPI } from '@/api'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 产品数据
    products: [],
    // 加载状态
    loading: false,
    // 错误状态管理
    error: null,
    // 联系表单数据
    contactForm: {
      name: '',
      phone: '',
      email: '',
      company: '',
      message: ''
    },
    // 当前选中的产品分类
    selectedCategory: 'all',
    // 登录状态管理
    isAuthenticated: false,
    user: null,
    rememberPassword: false
  },
  
  mutations: {
    SET_PRODUCTS(state, products) {
      state.products = products
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_ERROR(state) {
      state.error = null
    },
    UPDATE_CONTACT_FORM(state, formData) {
      state.contactForm = { ...state.contactForm, ...formData }
    },
    RESET_CONTACT_FORM(state) {
      state.contactForm = {
        name: '',
        phone: '',
        email: '',
        company: '',
        message: ''
      }
    },
    SET_SELECTED_CATEGORY(state, category) {
      state.selectedCategory = category
    },
    // 登录相关mutations
    SET_AUTHENTICATED(state, status) {
      state.isAuthenticated = status
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_REMEMBER_PASSWORD(state, remember) {
      state.rememberPassword = remember
    },
    // 产品相关mutations已简化，通过重新获取数据来同步状态
  },
  
  actions: {
    // 获取产品数据
    async fetchProducts({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      try {
        const response = await productsAPI.getProducts(params)
        console.log('API响应数据:', response) // 添加调试日志
        
        // 检查响应是否成功
        if (response.success === false) {
          throw new Error(response.message || '获取产品数据失败')
        }
        
        // 根据后端API返回格式，产品数据在response.products中
        let products = []
        if (response && response.products && Array.isArray(response.products)) {
          products = response.products
        } else if (response && response.data && Array.isArray(response.data)) {
          products = response.data
        } else if (Array.isArray(response)) {
          products = response
        }
        
        console.log('提取的产品数据:', products) // 添加调试日志
        commit('SET_PRODUCTS', products)
        return { success: true, data: products }
      } catch (error) {
        console.error('获取产品数据失败:', error.message)
        commit('SET_ERROR', error.message)
        commit('SET_PRODUCTS', []) // 设置为空数组而不是模拟数据
        return { success: false, message: error.message, data: [] }
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // 提交联系表单
    async submitContactForm({ commit, state }) {
      try {
        // 模拟API提交
        console.log('提交联系表单:', state.contactForm)
        // 实际项目中这里会调用API
        commit('RESET_CONTACT_FORM')
        return { success: true, message: '提交成功，我们会尽快联系您！' }
      } catch (error) {
        console.error('提交表单失败:', error)
        return { success: false, message: '提交失败，请稍后重试' }
      }
    },
    
    // 登录
    async login({ commit }, { username, password, remember }) {
      commit('CLEAR_ERROR')
      try {
        const response = await authAPI.login({ username, password })
        
        if (response.token) {
          // 获取管理员信息
          const adminInfo = await authAPI.getCurrentAdmin()
          const user = {
            username: adminInfo.admin?.username || username,
            email: adminInfo.admin?.email,
            loginTime: new Date().toISOString()
          }
          
          commit('SET_AUTHENTICATED', true)
          commit('SET_USER', user)
          commit('SET_REMEMBER_PASSWORD', remember)
          
          // 如果选择记住密码，保存到localStorage
          if (remember) {
            localStorage.setItem('adminCredentials', JSON.stringify({ username, password }))
          } else {
            localStorage.removeItem('adminCredentials')
          }
          localStorage.setItem('adminAuth', 'true')
          localStorage.setItem('adminUser', JSON.stringify(user))
          
          return { success: true, message: response.message || '登录成功' }
        } else {
          return { success: false, message: '登录失败，未获取到访问令牌' }
        }
      } catch (error) {
        console.error('登录失败:', error.message)
        commit('SET_ERROR', error.message || '登录失败，请检查用户名和密码')
        return { success: false, message: error.message || '登录失败，请稍后重试' }
      }
    },
    
    // 退出登录
    logout({ commit, state }) {
      // 调用API服务的logout方法清除本地存储
      authAPI.logout()
      
      commit('SET_AUTHENTICATED', false)
      commit('SET_USER', null)
      commit('SET_REMEMBER_PASSWORD', false)
      localStorage.removeItem('adminAuth')
      localStorage.removeItem('adminUser')
      
      // 如果没有选择记住密码，也清除凭据
      if (!state.rememberPassword) {
        localStorage.removeItem('adminCredentials')
      }
    },
    
    // 检查登录状态
    async checkAuthStatus({ commit }) {
      const isAuth = localStorage.getItem('adminAuth') === 'true'
      const token = localStorage.getItem('token')
      const savedUser = localStorage.getItem('adminUser')
      const credentials = localStorage.getItem('adminCredentials')
      
      // 如果没有基本的认证信息，直接清除状态
      if (!isAuth || !token) {
        this.dispatch('logout')
        return false
      }
      
      try {
        // 验证token有效性
        await authAPI.verifyToken()
        
        // Token有效，恢复认证状态
        commit('SET_AUTHENTICATED', true)
        
        // 恢复用户信息
        if (savedUser) {
          commit('SET_USER', JSON.parse(savedUser))
        } else if (credentials) {
          const { username } = JSON.parse(credentials)
          commit('SET_USER', { username, loginTime: new Date().toISOString() })
        }
        
        // 恢复记住密码状态
        if (credentials) {
          commit('SET_REMEMBER_PASSWORD', true)
        }
        
        return true
      } catch (error) {
        console.error('Token验证失败:', error.message)
        // Token无效，清除登录状态
        this.dispatch('logout')
        return false
      }
    },
    
    // 获取保存的登录凭据
    getSavedCredentials() {
      const credentials = localStorage.getItem('adminCredentials')
      return credentials ? JSON.parse(credentials) : null
    },
    
    // 添加产品
    async addProduct({ commit, dispatch }, productData) {
      commit('CLEAR_ERROR')
      const response = await productsAPI.createProduct(productData)
      
      if (response.success) {
        // 重新获取产品列表以确保数据同步
        await dispatch('fetchProducts')
        return { success: true, message: response.message || '产品添加成功', product: response.product }
      } else {
        commit('SET_ERROR', response.message || '产品添加失败')
        return { success: false, message: response.message || '产品添加失败' }
      }
    },
    
    // 更新产品
    async updateProduct({ commit, dispatch }, { id, productData }) {
      commit('CLEAR_ERROR')
      try {
        const response = await productsAPI.updateProduct(id, productData)
        
        // 后端返回 { message, product }，不包含 success 字段，这里以是否存在 product 字段作为成功判断
        if (response && (response.success === true || response.product)) {
          // 重新获取产品列表以确保数据同步
          await dispatch('fetchProducts')
          return { success: true, message: response.message || '产品更新成功', product: response.product || null }
        } else {
          return { success: false, message: response?.message || '产品更新失败' }
        }
      } catch (error) {
        console.error('更新产品失败:', error.message)
        commit('SET_ERROR', error.message || '更新产品失败')
        return { success: false, message: error.message || '产品更新失败，请稍后重试' }
      }
    },
    
    // 删除产品
    async deleteProduct({ commit, dispatch }, productId) {
      commit('CLEAR_ERROR')
      try {
        const response = await productsAPI.deleteProduct(productId)
        
        if (response.success) {
          // 重新获取产品列表以确保数据同步
          await dispatch('fetchProducts')
          return { success: true, message: response.message || '产品删除成功' }
        } else {
          return { success: false, message: response.message || '产品删除失败' }
        }
      } catch (error) {
        console.error('删除产品失败:', error.message)
        commit('SET_ERROR', error.message || '删除产品失败')
        return { success: false, message: error.message || '产品删除失败，请稍后重试' }
      }
    },
    
    // 清除错误状态
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  },
  
  getters: {
    // 根据分类筛选产品
    filteredProducts: (state) => {
      if (state.selectedCategory === 'all') {
        return state.products
      }
      return state.products.filter(product => product.category === state.selectedCategory)
    },
    
    // 获取所有产品分类（固定四个选项）
    productCategories: () => {
      return ['all', '钾类', '钠类', '酸类', '其他']
    }
  }
})